import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";

import LoginPage from "./components/Login/LoginPage";
import DashboardPage from "./components/Dashboard/DashboardPage";
import StripeCallbackPage from "./components/Dashboard/StripeCallbackPage";
import { validateClientGUID } from "./services/validateClientGUID";

function App() {
  const [warningTime, setWarningTime] = useState(null);

  const logoutTimerRef = useRef(null);
  const endTimeRef = useRef(null);
  const masterIntervalRef = useRef(null);

  const LOGOUT_TIMEOUT = 15 * 60 * 1000; // 15 mins
  const WARNING_SECONDS = 5;

  const SESSION_EXPIRES_KEY = "paxSessionExpires";

  const rawPaxData = localStorage.getItem("paxData");
  if (
    rawPaxData === "Error" ||
    rawPaxData === "error" ||
    rawPaxData === "[object Object]"
  ) {
    localStorage.removeItem("paxData");
    localStorage.removeItem(SESSION_EXPIRES_KEY);
  } else if (rawPaxData) {
    try {
      JSON.parse(rawPaxData);
    } catch {
      localStorage.removeItem("paxData");
      localStorage.removeItem(SESSION_EXPIRES_KEY);
    }
  }

  const logout = () => {
    localStorage.removeItem("paxData");
    localStorage.removeItem(SESSION_EXPIRES_KEY);

    setWarningTime(null);
    endTimeRef.current = null;

    const parts = window.location.pathname.split("/");
    const guid = parts[2];
    window.location.href = `/Pax/${guid}`;
  };

  const startLogoutTimer = () => {
    if (logoutTimerRef.current) return;

    const expiresAt = Date.now() + LOGOUT_TIMEOUT;
    endTimeRef.current = expiresAt;
    localStorage.setItem(SESSION_EXPIRES_KEY, expiresAt.toString());

    logoutTimerRef.current = setInterval(() => {
      const remaining = Math.max(
        Math.ceil((endTimeRef.current - Date.now()) / 1000),
        0,
      );

      if (remaining <= WARNING_SECONDS && remaining > 0) {
        setWarningTime(remaining);
      }

      if (remaining <= 0) {
        clearInterval(logoutTimerRef.current);
        logoutTimerRef.current = null;
        logout();
      }
    }, 1000);
  };

  // Initial load + resume session
  useEffect(() => {
    const paxData = localStorage.getItem("paxData");
    const expiresAt = Number(localStorage.getItem(SESSION_EXPIRES_KEY));

    if (paxData && expiresAt) {
      if (Date.now() > expiresAt) {
        logout();
        return;
      }

      endTimeRef.current = expiresAt;
      startLogoutTimer();
    }

    masterIntervalRef.current = setInterval(() => {
      if (!!localStorage.getItem("paxData") && !logoutTimerRef.current) {
        startLogoutTimer();
      }
    }, 30_000);

    return () => clearInterval(masterIntervalRef.current);
  }, []);

  // User activity listener
  useEffect(() => {
    const handler = () => {
      if (logoutTimerRef.current && warningTime === null) {
        const newExpiry = Date.now() + LOGOUT_TIMEOUT;
        endTimeRef.current = newExpiry;
        localStorage.setItem(SESSION_EXPIRES_KEY, newExpiry.toString());
      }
    };

    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "touchstart",
      "scroll",
    ];
    events.forEach((e) => window.addEventListener(e, handler));

    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, [warningTime]);

  const continueSession = () => {
    if (logoutTimerRef.current) {
      const newExpiry = Date.now() + LOGOUT_TIMEOUT;
      endTimeRef.current = newExpiry;
      localStorage.setItem(SESSION_EXPIRES_KEY, newExpiry.toString());
    }
    setWarningTime(null);
  };

  const GuidRoutes = () => {
    const { guid } = useParams();
    const [valid, setValid] = useState(null);

    const paxData = localStorage.getItem("paxData");
    const isLoggedIn = paxData && paxData !== "error" && paxData !== "Error";

    useEffect(() => {
      let mounted = true;

      const check = async () => {
        setValid(null);
        const ok = await validateClientGUID(guid);
        if (mounted) setValid(ok);
      };

      check();
      return () => (mounted = false);
    }, [guid]);

    if (valid === null)
      return <h2 style={{ textAlign: "center" }}>Validating client...</h2>;

    if (valid === false)
      return <h1 style={{ textAlign: "center" }}>404: Invalid client code!</h1>;

    return (
      <Routes>
        <Route
          index
          element={
            isLoggedIn ? (
              <Navigate to={`/${guid}/dashboard`} replace />
            ) : (
              <LoginPage />
            )
          }
        />

        <Route
          path="dashboard"
          element={
            isLoggedIn ? (
              <DashboardPage />
            ) : (
              <Navigate to={`/${guid}`} replace />
            )
          }
        />

        <Route path="*" element={<h1>404: Page Not Found</h1>} />
      </Routes>
    );
  };

  return (
    <Router basename="/Pax">
      {warningTime !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h2>Inactivity Warning</h2>
            <p>You will be logged out in {warningTime}s</p>
            <button onClick={continueSession}>Continue Session</button>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<h1>404: Client Not Found!</h1>} />
        <Route path=":guid/*" element={<GuidRoutes />} />
        <Route
          path=":guid/stripe/:action/:serviceGUId"
          element={<StripeCallbackPage />}
        />
        <Route path="*" element={<h1>404: Client Not Found!</h1>} />
      </Routes>
    </Router>
  );
}

export default App;
